import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearch } from 'wouter';

import useEvents from '../../hooks/useEvents';
import useSortedVenues from '../../hooks/useSortedVenues';

import EventCard from '../cards/Event';
import Select from '../fields/Select';
import Detector from './Detector';

import styles from '../../../styles/partials/events.module.css';

const propTypes = {
    query: PropTypes.shape({}),
    count: PropTypes.number,
    className: PropTypes.string,
};

function Events({ query: initialQuery = null, count = 12, className = null }) {
    const searchString = useSearch();
    const finalVenues = useSortedVenues();

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const baseQuery = useMemo(
        () => ({ upcoming: true, order: 'start_date', ...initialQuery }),
        [initialQuery],
    );
    const [query, setQuery] = useState(
        searchString !== null ? { ...queryString.parse(searchString), ...baseQuery } : baseQuery,
    );
    const { venue = null } = query || {};
    const onVenueChange = useCallback(
        (value) => {
            setQuery({ ...query, venue: value });
            setItems([]);
            setPage(1);
        },
        [query, setQuery],
    );

    const eventsQuery = useMemo(() => ({ count, query, page }), [query, page]);
    const { data: events = null, isLoading = false } = useEvents(eventsQuery);
    const empty = items.length === 0;
    const ended = events === null || events.length < count;

    useEffect(() => {
        if (events !== null) {
            setItems((prevItems) => uniqBy([...prevItems, ...events], 'id'));
        }
    }, [events]);

    const onPageEnd = useCallback(() => {
        if (!isLoading && !ended) {
            setPage(page + 1);
        }
    }, [page, setPage, isLoading, ended]);

    return (
        <div className={classNames([styles.container, { [className]: className !== null }])}>
            <div className={styles.filters}>
                <span>↓</span>
                <Select
                    className={styles.select}
                    value={venue}
                    options={finalVenues || []}
                    onChange={onVenueChange}
                    wide
                />
            </div>
            <div className={styles.list}>
                {(items || []).map((event) => (
                    <EventCard key={`event-${event?.id}`} className={styles.slide} item={event} />
                ))}
                {isLoading ? <EventCard className={styles.slide} placeholder /> : null}
                {!isLoading && !empty && !ended ? (
                    <Detector onEnter={onPageEnd}>Loading</Detector>
                ) : null}
                {!isLoading && empty ? (
                    <div className={styles.empty}>
                        <FormattedMessage
                            defaultMessage="No events found, come back soon!"
                            description="No events found"
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

Events.propTypes = propTypes;

export default Events;
